// Role run settings for when a run's connection hits a usage limit
// (checkpoints contract §3.5): wait, hand off, or hand off and else wait;
// the fallback agents in order of preference; how many handoffs a chain may
// have; and how often the runner is restarted after a passing API error.

import { useState } from 'react'
import type { OnQuotaSettings } from '@goblic/shared'
import type { AgentListItem } from '../lib/api-client'
import {
  MAX_FALLBACK_AGENTS,
  ON_QUOTA_MODE_OPTIONS,
  addFallback,
  exhaustionBadge,
  moveItem,
  onQuotaEffectiveNote,
} from '../lib/run-recovery'
import { shortId } from '../lib/format'
import { Field } from './ui'

const COUNTS = [0, 1, 2, 3, 4, 5]

export function RoleQuotaSettings({
  roleId,
  agents,
  onQuota,
  maxTransientRetries,
  onChange,
  onRetriesChange,
}: {
  /** null for a role that isn't saved yet (no agent can have it). */
  roleId: string | null
  /** All agents of the workspace; only those with this role can be fallbacks. */
  agents: AgentListItem[]
  onQuota: OnQuotaSettings
  maxTransientRetries: number
  onChange: (next: OnQuotaSettings) => void
  onRetriesChange: (next: number) => void
}) {
  const [adding, setAdding] = useState('')
  const set = <K extends keyof OnQuotaSettings>(key: K, value: OnQuotaSettings[K]) =>
    onChange({ ...onQuota, [key]: value })
  const ofRole = roleId ? agents.filter((a) => a.roleId === roleId) : []
  const addable = ofRole.filter((a) => !onQuota.fallbackAgentIds.includes(a.id))
  const modeHint = ON_QUOTA_MODE_OPTIONS.find((o) => o.value === onQuota.mode)?.hint
  const note = onQuotaEffectiveNote(onQuota)

  return (
    <div className="space-y-4">
      <div>
        <div
          role="radiogroup"
          aria-label="When the usage limit is reached"
          className="inline-flex rounded-control border border-line-strong bg-sunken p-0.5"
        >
          {ON_QUOTA_MODE_OPTIONS.map((option) => {
            const active = onQuota.mode === option.value
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => set('mode', option.value)}
                className={`rounded-[6px] px-3 py-1 text-sm font-medium transition-colors ${
                  active ? 'bg-surface text-ink shadow-sm' : 'text-ink-3 hover:text-ink'
                }`}
              >
                {option.label}
              </button>
            )
          })}
        </div>
        {modeHint ? <p className="mt-1.5 text-sm text-ink-3">{modeHint}</p> : null}
        {note ? <p className="mt-1 text-sm text-signal">{note}</p> : null}
      </div>

      <Field
        label="Fallback agents"
        hint={`In order of preference, up to ${MAX_FALLBACK_AGENTS}. Only agents with this role can take over.`}
      >
        {onQuota.fallbackAgentIds.length ? (
          <ol className="mb-2 divide-y divide-line rounded-control border border-line">
            {onQuota.fallbackAgentIds.map((id, index) => {
              const agent = agents.find((a) => a.id === id)
              const badge = agent ? exhaustionBadge(agent.connectionExhaustion) : null
              return (
                <li key={id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                  <span className="w-5 tabular-nums text-ink-3">{index + 1}.</span>
                  <span className="min-w-0 flex-1 truncate text-ink">
                    {agent ? agent.name : `Unknown agent ${shortId(id)}`}
                    {agent?.model ? <span className="text-ink-3"> · {agent.model}</span> : null}
                    {badge ? <span className="text-signal"> · {badge.inline}</span> : null}
                    {agent && roleId && agent.roleId !== roleId ? (
                      <span className="text-danger"> · has another role</span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    aria-label={`Move ${agent?.name ?? 'agent'} up`}
                    disabled={index === 0}
                    onClick={() => set('fallbackAgentIds', moveItem(onQuota.fallbackAgentIds, index, -1))}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    aria-label={`Move ${agent?.name ?? 'agent'} down`}
                    disabled={index === onQuota.fallbackAgentIds.length - 1}
                    onClick={() => set('fallbackAgentIds', moveItem(onQuota.fallbackAgentIds, index, 1))}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn-ghost btn-sm text-danger"
                    onClick={() =>
                      set(
                        'fallbackAgentIds',
                        onQuota.fallbackAgentIds.filter((other) => other !== id)
                      )
                    }
                  >
                    Remove
                  </button>
                </li>
              )
            })}
          </ol>
        ) : null}
        {roleId == null ? (
          <p className="text-sm text-ink-3">
            Save the role first, then pick agents that have it.
          </p>
        ) : onQuota.fallbackAgentIds.length >= MAX_FALLBACK_AGENTS ? null : addable.length ? (
          <div className="flex gap-2">
            <select
              aria-label="Add a fallback agent"
              className="field-input"
              value={adding}
              onChange={(event) => setAdding(event.target.value)}
            >
              <option value="">Add an agent…</option>
              {addable.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                  {agent.model ? ` (${agent.model})` : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-secondary"
              disabled={!adding}
              onClick={() => {
                set('fallbackAgentIds', addFallback(onQuota.fallbackAgentIds, adding))
                setAdding('')
              }}
            >
              Add
            </button>
          </div>
        ) : (
          <p className="text-sm text-ink-3">
            {ofRole.length ? 'Every agent with this role is listed.' : 'No agents have this role yet.'}
          </p>
        )}
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Handoffs per task step"
          htmlFor="role-max-handoffs"
          hint="Automatic and by hand together."
        >
          <select
            id="role-max-handoffs"
            className="field-input"
            value={onQuota.maxHandoffs}
            onChange={(event) => set('maxHandoffs', Number(event.target.value))}
          >
            {COUNTS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Retries after an API error"
          htmlFor="role-max-retries"
          hint="Restarts in the same sandbox, per run. 0 stops the run at once."
        >
          <select
            id="role-max-retries"
            className="field-input"
            value={maxTransientRetries}
            onChange={(event) => onRetriesChange(Number(event.target.value))}
          >
            {COUNTS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  )
}
